import React, { useState } from 'react';
import {
  MdTextFields,
  MdBarChart,
  MdHorizontalRule,
  MdInsertPageBreak,
  MdTitle,
} from 'react-icons/md';
import { FaTable, FaChartBar } from 'react-icons/fa';
import { IoIosClose } from 'react-icons/io';
import { AnimatePresence, motion } from 'framer-motion';
import Tooltip from '@mui/material/Tooltip';
import { PdfTag } from '../types/KidPdfGeneratorV2.types';

interface AddBlockChooserProps {
  onChoose: (type: PdfTag) => void;
}

const AddBlockChooser: React.FC<AddBlockChooserProps> = ({ onChoose }) => {
  const [open, setOpen] = useState(false);

  const buttons = [
    {
      type: 'text' as PdfTag,
      icon: MdTextFields,
      className:
        'group px-6 flex py-1 items-center justify-center rounded-md border-2 border-blue-300 bg-white text-blue-600 transition-all hover:border-blue-400 hover:bg-blue-50 hover:shadow-sm',
      title: 'Add Text Block',
    },
    {
      type: 'heading' as PdfTag,
      icon: MdTitle,
      className:
        'group px-6 flex py-1 items-center justify-center rounded-md border-2 border-cyan-300 bg-white text-cyan-600 transition-all hover:border-cyan-400 hover:bg-cyan-50 hover:shadow-sm',
      title: 'Add Heading',
    },
    {
      type: 'table' as PdfTag,
      icon: FaTable,
      className:
        'group px-6 flex py-1 items-center justify-center rounded-md border-2 border-rose-300 bg-white text-rose-600 transition-all hover:border-rose-400 hover:bg-rose-50 hover:shadow-sm',
      title: 'Add Table Block',
    },
    {
      type: 'sri_chart' as PdfTag,
      icon: MdBarChart,
      className:
        'group px-6  flex py-1 items-center justify-center rounded-md border-2 border-green-300 bg-white text-green-600 transition-all hover:border-green-400 hover:bg-green-50 hover:shadow-sm',
      title: 'Add SRI Block',
    },
    {
      type: 'bar_chart' as PdfTag,
      icon: FaChartBar,
      className:
        'group px-6 flex py-1 items-center justify-center rounded-md border-2 border-purple-300 bg-white text-purple-600 transition-all hover:border-purple-400 hover:bg-purple-50 hover:shadow-sm',
      title: 'Add Chart Block',
    },
    {
      type: 'divider' as PdfTag,
      icon: MdHorizontalRule,
      className:
        'group px-6 flex py-1 items-center justify-center rounded-md border-2 border-gray-300 bg-white text-gray-600 transition-all hover:border-gray-400 hover:bg-gray-50 hover:shadow-sm',
      title: 'Add Divider',
    },
    {
      type: 'page_break' as PdfTag,
      icon: MdInsertPageBreak,
      className:
        'group px-6 flex py-1 items-center justify-center rounded-md border-2 border-slate-300 bg-white text-slate-600 transition-all hover:border-slate-400 hover:bg-slate-50 hover:shadow-sm',
      title: 'Add Page Break',
    },
  ];

  return (
    <AnimatePresence>
      {!open ? (
        <motion.button
          key="add-block-button"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0 } }}
          transition={{ duration: 0.05, ease: 'easeOut' }}
          type="button"
          onClick={() => setOpen(true)}
          className="my-2 w-full rounded-md border border-dashed border-gray-300 bg-white py-2 text-sm text-gray-600 hover:border-gray-400"
        >
          + Add Block
        </motion.button>
      ) : (
        <motion.div
          key="add-block-chooser"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0 } }}
          transition={{ duration: 0.05, ease: 'easeOut' }}
          className="relative my-2 flex items-center justify-center rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
        >
          {/* Close button in top right */}
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.05, ease: 'easeOut' }}
            type="button"
            onClick={() => setOpen(false)}
            className="absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full border border-gray-300 bg-white text-gray-500 transition-all hover:border-gray-400 hover:bg-gray-50 hover:text-gray-700"
            title="Close"
          >
            <IoIosClose className="h-4 w-4" />
          </motion.button>

          {/* Block type buttons - Medium style circular icons */}
          <div className="grid w-fit grid-cols-2 items-center justify-items-center gap-1 sm:grid-cols-3 md:grid-cols-4">
            {buttons.map((button, index) => {
              const IconComponent = button.icon;
              return (
                <Tooltip
                  key={button.type}
                  title={button.title}
                  arrow
                  placement="top"
                  slotProps={{
                    arrow: {
                      className: 'text-gray-700',
                    },
                  }}
                >
                  <motion.button
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      delay: index * 0.02,
                      duration: 1,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    onClick={() => {
                      onChoose(button.type);
                      setOpen(false);
                    }}
                    className={button.className}
                  >
                    <IconComponent className="h-5 w-5" />
                  </motion.button>
                </Tooltip>
              );
            })}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddBlockChooser;
